import type { SchedulingFlowInitialData } from "@leagueplatform/care-finder";

// QUEST-2796 bridge — captures the SDK's `openOverlay` (only accessible via
// the `useStartSchedulingFlow` React hook) so the out-of-React action handler
// can call it. See the PR body for the reasoning.

type OpenOverlayFn = (initialData?: SchedulingFlowInitialData) => void;

const DEFAULT_BRIDGE_WAIT_TIMEOUT_MS = 5000;

let openOverlayHandle: OpenOverlayFn | null = null;
const waiters = new Set<(fn: OpenOverlayFn) => void>();

export const setSchedulingFlowOpenOverlayHandle = (
  fn: OpenOverlayFn | null
): void => {
  openOverlayHandle = fn;
  if (fn) {
    const pending = Array.from(waiters);
    waiters.clear();
    pending.forEach((waiter) => waiter(fn));
  }
};

export const getSchedulingFlowOpenOverlayHandle = (): OpenOverlayFn | null =>
  openOverlayHandle;

// QUEST-2866 — the SDK overlay closes itself as soon as `location.key`
// changes after it opened, so a deeplink that lands on the flow has to
// navigate FIRST and open the overlay only once the destination is live.
// The request carries the location it was made on and is only handed out
// somewhere else, which holds whether the consumer mounts before or after
// the requester. It is parked in module scope, not history state, so a
// page reload on the destination doesn't re-open the flow.
type SchedulingFlowOpenRequest = {
  initialData: SchedulingFlowInitialData | null;
  requestedAtLocationKey: string;
};

let openRequest: SchedulingFlowOpenRequest | null = null;

export const requestSchedulingFlowOpen = (
  requestedAtLocationKey: string,
  initialData?: SchedulingFlowInitialData
): void => {
  openRequest = {
    initialData: initialData ?? null,
    requestedAtLocationKey,
  };
};

export const resetSchedulingFlowOpenRequest = (): void => {
  openRequest = null;
};

export const consumeSchedulingFlowOpenRequest = (
  currentLocationKey: string
): SchedulingFlowOpenRequest | null => {
  if (!openRequest) return null;
  if (openRequest.requestedAtLocationKey === currentLocationKey) return null;
  const request = openRequest;
  openRequest = null;
  return request;
};

// Async getter that resolves once the bridge mounts, or rejects after
// `timeoutMs` — used by the action handler to survive the lazy-load race
// window (F-8d70) without leaking orphaned backend sessions.
export const awaitSchedulingFlowOpenOverlayHandle = (
  timeoutMs: number = DEFAULT_BRIDGE_WAIT_TIMEOUT_MS
): Promise<OpenOverlayFn> => {
  if (openOverlayHandle) return Promise.resolve(openOverlayHandle);
  return new Promise<OpenOverlayFn>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout>;
    const waiter = (fn: OpenOverlayFn) => {
      clearTimeout(timer);
      resolve(fn);
    };
    timer = setTimeout(() => {
      waiters.delete(waiter);
      reject(
        new Error(
          `Scheduling flow bridge did not mount within ${timeoutMs}ms — QuestSchedulingFlowBridge must be mounted at the app root`
        )
      );
    }, timeoutMs);
    waiters.add(waiter);
  });
};
